import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { collection, query, where, getDocs, doc, updateDoc, addDoc } from "firebase/firestore";
import { signInWithEmailAndPassword, getAuth } from "firebase/auth";
import { db, auth } from "../config/firebase";

const TeacherLogin = () => {
  const [loginMethod, setLoginMethod] = useState("pin"); // "pin" or "email"
  const [pin, setPin] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [fetchingInfo, setFetchingInfo] = useState(false);
  const [error, setError] = useState("");
  const [schoolId, setSchoolId] = useState(null);
  const [deviceId, setDeviceId] = useState(null);
  const [schoolName, setSchoolName] = useState("");
  const [deviceName, setDeviceName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  // Get device and school info from previous step or fetch if missing
  useEffect(() => {
    const state = location.state || {};
    if (state.schoolId && state.deviceId) {
      setSchoolId(state.schoolId);
      setDeviceId(state.deviceId);
      setSchoolName(state.schoolName || "");
      setDeviceName(state.deviceName || "");
      return;
    }
    // If missing, try to fetch from Firestore using the authenticated user
    const fetchInfo = async () => {
      setFetchingInfo(true);
      const currAuth = getAuth();
      const user = currAuth.currentUser;
      if (user && user.email) {
        // 1. Get teacher user record
        const teacherQ = query(
          collection(db, "users"),
          where("email", "==", user.email),
          where("role", "==", "teacher"),
          where("active", "==", true)
        );
        const teacherSnap = await getDocs(teacherQ);
        if (!teacherSnap.empty) {
          const teacherData = teacherSnap.docs[0].data();
          setSchoolId(teacherData.school_id);

          // 2. Get school name (optional)
          const schoolQ = query(
            collection(db, "schools"),
            where("__name__", "==", teacherData.school_id)
          );
          const schoolSnap = await getDocs(schoolQ);
          if (!schoolSnap.empty) {
            setSchoolName(schoolSnap.docs[0].data().name);
          }

          // 3. Get device for this school
          const deviceQ = query(
            collection(db, "devices"),
            where("school_id", "==", teacherData.school_id),
            where("active", "==", true)
          );
          const deviceSnap = await getDocs(deviceQ);
          if (!deviceSnap.empty) {
            const deviceData = deviceSnap.docs[0].data();
            setDeviceId(deviceSnap.docs[0].id);
            setDeviceName(deviceData.device_name);
          }
        }
      }
      setFetchingInfo(false);
    };
    fetchInfo();
  }, [location.state]);

  // Redirect to device login if info is still missing after fetch
  useEffect(() => {
    if (!fetchingInfo && (!schoolId || !deviceId)) {
      // Only redirect if not currently fetching
      navigate("/");
    }
  }, [fetchingInfo, schoolId, deviceId, navigate]);

  if (fetchingInfo) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen" style={{ backgroundColor: '#f8f9fa' }}>
        <div className="flex flex-col items-center">
          <div className="w-16 h-16 border-4 border-orange-400 border-t-transparent rounded-full animate-spin mb-6" style={{ borderTopColor: '#ff6b35', borderRightColor: '#ff9500' }} />
          <div className="text-xl font-semibold" style={{ color: '#ff6b35' }}>Loading school info…</div>
        </div>
      </div>
    );
  }

  const handleTeacherLogin = async () => {
    if (loginMethod === "pin" && pin.length < 4) {
      setError("PIN must be at least 4 digits");
      return;
    }

    if (loginMethod === "email" && (!email.trim() || !password.trim())) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      let teacherQuery;
      let teacherDoc;
      let teacherData;

      if (loginMethod === "pin") {
        // PIN-based login
        teacherQuery = query(
          collection(db, "users"),
          where("role", "==", "teacher"),
          where("school_id", "==", schoolId),
          where("pin_code", "==", pin),
          where("active", "==", true)
        );

        const querySnapshot = await getDocs(teacherQuery);
        
        if (querySnapshot.empty) {
          setError("Invalid PIN or teacher not found");
          setLoading(false);
          return;
        }

        teacherDoc = querySnapshot.docs[0];
        teacherData = teacherDoc.data();

      } else {
        // Email/Password login with Firebase Auth
        try {
          const userCredential = await signInWithEmailAndPassword(auth, email, password);
          const user = userCredential.user;

          // Find teacher record using email
          teacherQuery = query(
            collection(db, "users"),
            where("role", "==", "teacher"),
            where("school_id", "==", schoolId),
            where("email", "==", email),
            where("active", "==", true)
          );

          const querySnapshot = await getDocs(teacherQuery);
          
          if (querySnapshot.empty) {
            setError("Teacher not found for this account");
            setLoading(false);
            return;
          }

          teacherDoc = querySnapshot.docs[0];
          teacherData = teacherDoc.data();

          console.log("Teacher found:", teacherDoc.id);
          console.log("Firebase UID:", user.uid);
          console.log("Teacher data:", teacherData);

          // Check auth_type (if field exists)
          if (teacherData.auth_type && teacherData.auth_type !== "email_password") {
            setError("Invalid authentication method for this teacher");
            setLoading(false);
            return;
          }

        } catch (authError) {
          console.error("Firebase Auth error:", authError);
          
          // Handle specific Firebase Auth errors
          if (authError.code === 'auth/user-not-found') {
            setError("No account found with this email");
          } else if (authError.code === 'auth/wrong-password') {
            setError("Invalid password");
          } else if (authError.code === 'auth/invalid-email') {
            setError("Invalid email format");
          } else if (authError.code === 'auth/user-disabled') {
            setError("This account has been disabled");
          } else {
            setError("Authentication failed. Please try again.");
          }
          setLoading(false);
          return;
        }
      }

      // Create teacher session
      const sessionData = {
        device_id: deviceId,
        teacher_user_id: teacherDoc.id,
        school_id: schoolId,
        employee_id_used: teacherData.employee_id || "",
        pin_code_used: loginMethod === "pin" ? pin : null,
        email_used: loginMethod === "email" ? email : null,
        auth_method: loginMethod,
        signed_in_at: new Date().toISOString(),
        signed_out_at: null,
        active: true,
        auto_signout_at: new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString(), // 4 hours from now
        session_type: "teacher_monitor"
      };

      // Add session to database
      const sessionRef = await addDoc(collection(db, "teacher_sessions"), sessionData);

      // Update device to show current session
      const deviceRef = doc(db, "devices", deviceId);
      await updateDoc(deviceRef, {
        current_teacher_session: sessionRef.id,
        last_used: new Date().toISOString()
      });

      // Navigate to dashboard with complete session info
      navigate("/dashboard", { 
        state: { 
          teacherId: teacherDoc.id,
          teacherData: teacherData,
          schoolId: schoolId,
          deviceId: deviceId,
          sessionId: sessionRef.id,
          schoolName: schoolName,
          deviceName: deviceName,
          classIds: teacherData.class_ids || [],
          authMethod: loginMethod
        } 
      });

    } catch (error) {
      console.error("Teacher login error:", error);
      setError("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleButtonClick = (number) => {
    if (pin.length < 8) { // Limit pin to 8 digits as per schema
      setPin((prevPin) => prevPin + number);
    }
  };

  const handleDelete = () => {
    setPin((prevPin) => prevPin.slice(0, -1));
  };

  const handleClear = () => {
    setPin("");
  };

  const handleBackToDevice = () => {
    navigate("/");
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleTeacherLogin();
    }
  };

  // SafeLine Pickup Color Scheme
  const safeLineColors = {
    flameOrange: '#ff6b35',
    brightOrange: '#ff9500', 
    deepOrange: '#e55100',
    orangeTint: '#fff4f0',
    pureWhite: '#ffffff',
    lightGray: '#f8f9fa',
    coolGray: '#e9ecef',
    mediumGray: '#dee2e6',
    emerald: '#10b981',
    lightEmerald: '#d1fae5',
    forestGreen: '#047857',
    amber: '#f59e0b',
    lightAmber: '#fef3c7',
    darkAmber: '#d97706',
    red: '#ef4444',
    lightRed: '#fee2e2',
    darkRed: '#dc2626',
    darkCharcoal: '#1f2937',
    mediumCharcoal: '#374151',
    lightCharcoal: '#6b7280',
    mutedGray: '#9ca3af'
  };

  return (
    <div 
      className="teacher-login-container flex flex-col items-center justify-center min-h-screen py-8"
      style={{ backgroundColor: safeLineColors.lightGray }}
    >
      <div 
        className="p-8 rounded-xl shadow-2xl w-96 relative overflow-hidden my-8"
        style={{ 
          backgroundColor: safeLineColors.pureWhite,
          border: `1px solid ${safeLineColors.coolGray}`,
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          maxHeight: '90vh',
          overflowY: 'auto'
        }}
      >
        {/* Orange accent bar */}
        <div 
          className="absolute top-0 left-0 right-0 h-1"
          style={{ 
            background: `linear-gradient(90deg, ${safeLineColors.flameOrange}, ${safeLineColors.brightOrange})`
          }}
        />

        {/* Header with school and device info */}
        <div className="text-center mb-8">
          <div 
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ backgroundColor: safeLineColors.orangeTint }}
          >
            <svg className="w-8 h-8" style={{ color: safeLineColors.flameOrange }} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
            </svg>
          </div>
          <h1 
            className="text-2xl font-bold mb-2"
            style={{ color: safeLineColors.darkCharcoal }}
          >
            Teacher Login
          </h1>
          <div 
            className="text-sm space-y-1"
            style={{ color: safeLineColors.mediumCharcoal }}
          >
            <p><strong>School:</strong> {schoolName}</p>
            <p><strong>Device:</strong> {deviceName}</p>
          </div>
        </div>

        {error && (
          <div 
            className="px-4 py-3 rounded-lg mb-6 border"
            style={{ 
              backgroundColor: safeLineColors.lightRed,
              borderColor: safeLineColors.red,
              color: safeLineColors.darkRed
            }}
          >
            <div className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
              </svg>
              {error}
            </div>
          </div>
        )}

        {/* Login method selection */}
        <div className="mb-8">
          <div 
            className="flex rounded-lg p-1"
            style={{ backgroundColor: safeLineColors.lightGray }}
          >
            <button
              className="flex-1 py-3 px-4 rounded-md text-sm font-semibold transition-all duration-200"
              style={{
                backgroundColor: loginMethod === "pin" ? safeLineColors.flameOrange : 'transparent',
                color: loginMethod === "pin" ? safeLineColors.pureWhite : safeLineColors.mediumCharcoal,
                boxShadow: loginMethod === "pin" ? `0 2px 8px rgba(255, 107, 53, 0.3)` : 'none'
              }}
              onClick={() => {
                setLoginMethod("pin");
                setError("");
              }}
              onMouseEnter={(e) => {
                if (loginMethod !== "pin") {
                  e.target.style.backgroundColor = safeLineColors.orangeTint;
                }
              }}
              onMouseLeave={(e) => {
                if (loginMethod !== "pin") {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm6-9h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM8.9 6c0-1.71 1.39-3.1 3.1-3.1s3.1 1.39 3.1 3.1v2H8.9V6z"/>
                </svg>
                PIN Login
              </div>
            </button>
            <button
              className="flex-1 py-3 px-4 rounded-md text-sm font-semibold transition-all duration-200"
              style={{
                backgroundColor: loginMethod === "email" ? safeLineColors.flameOrange : 'transparent',
                color: loginMethod === "email" ? safeLineColors.pureWhite : safeLineColors.mediumCharcoal,
                boxShadow: loginMethod === "email" ? `0 2px 8px rgba(255, 107, 53, 0.3)` : 'none'
              }}
              onClick={() => {
                setLoginMethod("email");
                setError("");
              }}
              onMouseEnter={(e) => {
                if (loginMethod !== "email") {
                  e.target.style.backgroundColor = safeLineColors.orangeTint;
                }
              }}
              onMouseLeave={(e) => {
                if (loginMethod !== "email") {
                  e.target.style.backgroundColor = 'transparent';
                }
              }}
            >
              <div className="flex items-center justify-center">
                <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                Email Login
              </div>
            </button>
          </div>
        </div>

        {/* PIN Login Form */}
        {loginMethod === "pin" && (
          <>
            {/* PIN Display */}
            <div className="mb-8 text-center">
              <label 
                className="block text-sm font-semibold mb-4"
                style={{ color: safeLineColors.darkCharcoal }}
              >
                Enter Teacher PIN (6-8 digits)
              </label>
              <div 
                className="text-3xl font-mono rounded-xl p-6 min-h-[80px] flex items-center justify-center border-2"
                style={{ 
                  backgroundColor: safeLineColors.orangeTint,
                  borderColor: safeLineColors.coolGray,
                  color: safeLineColors.darkCharcoal
                }}
              >
                {pin.replace(/./g, "●") || "●●●●●●"}
              </div>
            </div>

            {/* Numpad */}
            <div className="grid grid-cols-3 gap-3 mb-6">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                <button
                  key={num}
                  className="p-4 rounded-xl text-xl font-bold transition-all duration-150 border-2"
                  style={{
                    backgroundColor: safeLineColors.pureWhite,
                    borderColor: safeLineColors.coolGray,
                    color: safeLineColors.darkCharcoal,
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = safeLineColors.flameOrange;
                      e.target.style.color = safeLineColors.pureWhite;
                      e.target.style.borderColor = safeLineColors.flameOrange;
                      e.target.style.transform = 'translateY(-2px)';
                      e.target.style.boxShadow = '0 4px 8px rgba(255, 107, 53, 0.3)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!loading) {
                      e.target.style.backgroundColor = safeLineColors.pureWhite;
                      e.target.style.color = safeLineColors.darkCharcoal;
                      e.target.style.borderColor = safeLineColors.coolGray;
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                    }
                  }}
                  onClick={() => handleButtonClick(num.toString())}
                  disabled={loading}
                >
                  {num}
                </button>
              ))}
              
              <button
                className="p-4 rounded-xl text-lg font-bold transition-all duration-150 border-2"
                style={{
                  backgroundColor: safeLineColors.mutedGray,
                  borderColor: safeLineColors.mutedGray,
                  color: safeLineColors.pureWhite
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = safeLineColors.lightCharcoal;
                    e.target.style.borderColor = safeLineColors.lightCharcoal;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = safeLineColors.mutedGray;
                    e.target.style.borderColor = safeLineColors.mutedGray;
                  }
                }}
                onClick={handleClear}
                disabled={loading}
              >
                Clear
              </button>
              
              <button
                className="p-4 rounded-xl text-xl font-bold transition-all duration-150 border-2"
                style={{
                  backgroundColor: safeLineColors.pureWhite,
                  borderColor: safeLineColors.coolGray,
                  color: safeLineColors.darkCharcoal,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = safeLineColors.flameOrange;
                    e.target.style.color = safeLineColors.pureWhite;
                    e.target.style.borderColor = safeLineColors.flameOrange;
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(255, 107, 53, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = safeLineColors.pureWhite;
                    e.target.style.color = safeLineColors.darkCharcoal;
                    e.target.style.borderColor = safeLineColors.coolGray;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
                  }
                }}
                onClick={() => handleButtonClick("0")}
                disabled={loading}
              >
                0
              </button>
              
              <button
                className="p-4 rounded-xl text-xl font-bold transition-all duration-150 border-2"
                style={{
                  backgroundColor: safeLineColors.red,
                  borderColor: safeLineColors.red,
                  color: safeLineColors.pureWhite
                }}
                onMouseEnter={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = safeLineColors.darkRed;
                    e.target.style.borderColor = safeLineColors.darkRed;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!loading) {
                    e.target.style.backgroundColor = safeLineColors.red;
                    e.target.style.borderColor = safeLineColors.red;
                  }
                }}
                onClick={handleDelete}
                disabled={loading}
              >
                ←
              </button>
            </div>
          </>
        )}

        {/* Email Login Form */}
        {loginMethod === "email" && (
          <div className="space-y-6 mb-8">
            <div>
              <label 
                className="block text-sm font-semibold mb-3"
                style={{ color: safeLineColors.darkCharcoal }}
              >
                Teacher Email
              </label>
              <div className="relative">
                <div 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  style={{ color: safeLineColors.flameOrange }}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                  </svg>
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-12 pr-4 py-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: safeLineColors.pureWhite,
                    border: `2px solid ${safeLineColors.coolGray}`,
                    color: safeLineColors.darkCharcoal,
                    fontSize: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = safeLineColors.flameOrange;
                    e.target.style.boxShadow = `0 0 0 3px ${safeLineColors.orangeTint}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = safeLineColors.coolGray;
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="Enter your email"
                  disabled={loading}
                />
              </div>
            </div>

            <div>
              <label 
                className="block text-sm font-semibold mb-3"
                style={{ color: safeLineColors.darkCharcoal }}
              >
                Password
              </label>
              <div className="relative">
                <div 
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  style={{ color: safeLineColors.flameOrange }}
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6 9c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/>
                  </svg>
                </div>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="w-full pl-12 pr-4 py-4 rounded-lg transition-all duration-200 focus:outline-none focus:ring-2"
                  style={{
                    backgroundColor: safeLineColors.pureWhite,
                    border: `2px solid ${safeLineColors.coolGray}`,
                    color: safeLineColors.darkCharcoal,
                    fontSize: '16px'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = safeLineColors.flameOrange;
                    e.target.style.boxShadow = `0 0 0 3px ${safeLineColors.orangeTint}`;
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = safeLineColors.coolGray;
                    e.target.style.boxShadow = 'none';
                  }}
                  placeholder="Enter your password"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={handleBackToDevice}
            disabled={loading}
            className="py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 border-2"
            style={{
              backgroundColor: safeLineColors.pureWhite,
              borderColor: safeLineColors.coolGray,
              color: safeLineColors.mediumCharcoal
            }}
            onMouseEnter={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = safeLineColors.lightGray;
                e.target.style.borderColor = safeLineColors.mediumCharcoal;
              }
            }}
            onMouseLeave={(e) => {
              if (!loading) {
                e.target.style.backgroundColor = safeLineColors.pureWhite;
                e.target.style.borderColor = safeLineColors.coolGray;
              }
            }}
          >
            Back
          </button>
          
          <button
            onClick={handleTeacherLogin}
            disabled={loading || (loginMethod === "pin" && pin.length < 4) || (loginMethod === "email" && (!email.trim() || !password.trim()))}
            className="py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-200 relative overflow-hidden"
            style={{
              background: (loading || (loginMethod === "pin" && pin.length < 4) || (loginMethod === "email" && (!email.trim() || !password.trim())))
                ? safeLineColors.mutedGray 
                : `linear-gradient(135deg, ${safeLineColors.emerald}, ${safeLineColors.forestGreen})`,
              color: safeLineColors.pureWhite,
              border: 'none',
              cursor: (loading || (loginMethod === "pin" && pin.length < 4) || (loginMethod === "email" && (!email.trim() || !password.trim()))) ? 'not-allowed' : 'pointer',
              boxShadow: (loading || (loginMethod === "pin" && pin.length < 4) || (loginMethod === "email" && (!email.trim() || !password.trim())))
                ? 'none' 
                : `0 4px 15px rgba(16, 185, 129, 0.3)`,
              transform: (loading || (loginMethod === "pin" && pin.length < 4) || (loginMethod === "email" && (!email.trim() || !password.trim()))) ? 'none' : 'translateY(0)'
            }}
            onMouseEnter={(e) => {
              if (!(loading || (loginMethod === "pin" && pin.length < 4) || (loginMethod === "email" && (!email.trim() || !password.trim())))) {
                e.target.style.background = `linear-gradient(135deg, ${safeLineColors.forestGreen}, ${safeLineColors.emerald})`;
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = `0 6px 20px rgba(16, 185, 129, 0.4)`;
              }
            }}
            onMouseLeave={(e) => {
              if (!(loading || (loginMethod === "pin" && pin.length < 4) || (loginMethod === "email" && (!email.trim() || !password.trim())))) {
                e.target.style.background = `linear-gradient(135deg, ${safeLineColors.emerald}, ${safeLineColors.forestGreen})`;
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = `0 4px 15px rgba(16, 185, 129, 0.3)`;
              }
            }}
          >
            {loading ? (
              <div className="flex items-center justify-center">
                <div 
                  className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-3"
                />
                Signing In...
              </div>
            ) : (
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                Sign In
              </div>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TeacherLogin;